import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  Paper,
  Radio,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ClaimTabs from "../../../claims/common/ClaimTabs";
import activeResume from "../../../../../assets/images/activeResume.svg";
import activeDeceased from "../../../../../assets/images/activeDeceased.svg";
import acticeServices from "../../../../../assets/images/activeServices.svg";
import activeLocation from "../../../../../assets/images/activeLocation.svg";
import activeDocuments from "../../../../../assets/images/activeDocuments.svg";
import activeVehicle from "../../../../../assets/images/activeVehicle.svg";
import activeDriving from "../../../../../assets/images/activeDriving.svg";
import activePricing from "../../../../../assets/images/activePricing.svg";
import inactiveDocuments from "../../../../../assets/images/inactiveDocuments.svg";
import inactiveLocation from "../../../../../assets/images/inactiveLocation.svg";
import inactiveServices from "../../../../../assets/images/inactiveServices.svg";
import inactiveDeceased from "../../../../../assets/images/inactiveDeceased.svg";
import inactiveResume from "../../../../../assets/images/inactiveResume.svg";
import inactiveVehicle from "../../.././../../assets/images/inactiveVehicle.svg";
import inactiveDriving from "../../../../../assets/images/inactiveDriving.svg"; 
import inactivePricing from "../../../../../assets/images/inactivePricing.svg";
import InformantDetails from "./informant-details/InformantDetails";
import DeceasedDetails from "./deceased-details/DeceasedDetails";
import RequestedServices from "./requested-services/RequestedServices";
import BodyLocation from "./body-location/BodyLocation";
import Documents from "./documents/Documents";
import PickupDetails from "./pickup-details/PickupDetails";
import { useCaseStore } from "../../../../../store/useCaseStore";
import { useBodyRemovalsStore } from "../../../../../store/useBodyRemovalsStore";
import Package from "./package-pricing/Package";
import ButtonComp from "../../../../../components/Button";
import { useQuotationStore } from "../../../../../store/useQuotationStore";
import { BorderColor as BorderColorIcon } from "../../../../../components/icons";
import VerifiedPopup from "../../../../../components/VerifiedPopup";
import { useVerifiedStore } from "../../../../../store/useVerifiedStore";
import { useToast } from "../../../../../hooks/useToast";
import {
  acceptBodyRemovalDispatch,
  confirmBodyCollection,
  createRemovalOrder,
  deliverBodyRemoval,
  getBodyRemovalDetails,
  markBodyRemovalArrivedAtMortuary,
  markBodyRemovalArrivedAtScene,
  markBodyRemovalEnRoute,
  getCaseDetails,
  getOperationalLocations,
  getDocument,
  submitCase,
  updateCase,
  searchClaim,
  convertCashClientToPolicyholder,
} from "../../../../../services/dashboard";
import { useAuthStore } from "../../../../../store/useAuthStore";
import { useSessionStore } from "../../../../../store/useSessionStore";
import { useCaseCreationStore } from "../../../../../store/useCaseCreationStore";
import BodyRemovalForm from "./body-removal-form/BodyRemovalForm";
import { useUserStore } from "../../../../../store/useUserStore";
import {
  OperationsSectionHeader,
  operationsDetailPaperSx,
} from "../../../ifas/components/OperationsUI";
import { isIfasDriverRole } from "../../../../../utils/portal";
import { hasIfasCapability, isRemovalCaseInitiatorRole } from "../../../../../utils/ifasAccess";

export default function CaseDetails(props: any) {
  const {
    viewMode,
    refreshBodyRemovals,
    onCaseHistoryClicked,
    onBodyRemovalHistoryClicked,
    resetToCaseList,
    resetToBodyRemovalList,
  } = props;
  const [activeTab, setActiveTab] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCreatingRemovalOrder, setIsCreatingRemovalOrder] = useState(false);
  const [isDelivering, setIsDelivering] = useState(false);
  const [showDeliveryDialog, setShowDeliveryDialog] = useState(false);
  const [deliveryDetails, setDeliveryDetails] = useState({
    driverId: "",
    receivedBy: "",
    kmInArrivalAtMortuary: "",
    remarks: "",
  });
  const [isConfirmingBodyCollection, setIsConfirmingBodyCollection] =
    useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [shouldResetAfterSubmit, setShouldResetAfterSubmit] = useState(false);
  const [shouldResetToBodyRemovalList, setShouldResetToBodyRemovalList] =
    useState(false);
  const refreshedCaseNumberRef = useRef<string | null>(null);

  const { selectedCase, setSelectedCase } = useCaseStore();
  const { selectedBodyRemoval, setSelectedBodyRemoval } = useBodyRemovalsStore();
  const { selectedQuotation } = useQuotationStore();
  const { showVerified, setShowVerified, verifiedLabel, setVerifiedLabel } =
    useVerifiedStore();
  const { showInfoToast, showSuccessToast } = useToast();
  const { userId, driverId: authDriverId, first_name, last_name, user_name, email } = useAuthStore();
  const role = useUserStore((state) => state.role);
  const { userRole, userIdData } = useSessionStore();
  const effectiveRole = userRole || role;
  const currentUserId = userId || userIdData;
  const canWriteIfasCase = hasIfasCapability(effectiveRole, "caseWrite");
  const canSubmitIfasCase = hasIfasCapability(effectiveRole, "caseSubmit");
  const canConvertCashClient = hasIfasCapability(effectiveRole, "caseConvertPolicy");
  const [showConversionDialog, setShowConversionDialog] = useState(false);
  const [conversionPolicyNumber, setConversionPolicyNumber] = useState("");
  const [conversionReason, setConversionReason] = useState("");
  const [conversionClaims, setConversionClaims] = useState<any[]>([]);
  const [selectedConversionClaim, setSelectedConversionClaim] = useState<any>(null);
  const [conversionError, setConversionError] = useState("");
  const [isSearchingConversionClaim, setIsSearchingConversionClaim] = useState(false);
  const [isConvertingClient, setIsConvertingClient] = useState(false);
  const {
    informantDetails,
    deceasedDetails,
    deceasedLocation,
    mortuaryName,
    packageSelection,
    documents: localDocuments,
    setInformantDetails,
    setDeceasedDetails,
    setDeceasedLocation,
    setMortuaryName,
    setPackageSelection,
    resetDocuments,
  } = useCaseCreationStore();
  const [branchOptions, setBranchOptions] = useState<any[]>([]);
  const [mortuaryOptions, setMortuaryOptions] = useState<any[]>([]);

  useEffect(() => {
    let active = true;
    void Promise.all([
      getOperationalLocations("BRANCH"),
      getOperationalLocations("MORTUARY"),
    ]).then(([branchesResponse, mortuariesResponse]: any[]) => {
      if (active) {
        setBranchOptions(branchesResponse?.status === 200 && Array.isArray(branchesResponse.data) ? branchesResponse.data : []);
        setMortuaryOptions(mortuariesResponse?.status === 200 && Array.isArray(mortuariesResponse.data) ? mortuariesResponse.data : []);
      }
    });
    return () => {
      active = false;
    };
  }, []);

  const claimantData =
    viewMode === "QUOTATIONS_DETAILS"
      ? selectedQuotation?.claimant
      : selectedCase?.claimant;

  const deceasedData =
    viewMode === "BODY_REMOVAL"
      ? selectedBodyRemoval?.deceased
      : viewMode === "QUOTATIONS_DETAILS"
        ? selectedQuotation?.deceased
        : selectedCase?.deceased;

  const bodyLocationData =
    viewMode === "QUOTATIONS_DETAILS"
      ? selectedQuotation?.bodyRemoval
      : selectedCase?.bodyRemoval;

  const documentData =
    viewMode === "BODY_REMOVAL"
      ? selectedBodyRemoval?.documentResponseDtoList
      : viewMode === "QUOTATIONS_DETAILS"
        ? selectedQuotation?.documentResponseDtoList
        : selectedCase?.documentResponseDtoList;

  let uniqueId: any;

  if (viewMode === "BODY_REMOVAL") {
    uniqueId = selectedBodyRemoval?.isManual
      ? selectedBodyRemoval?.caseId
      : selectedBodyRemoval?.claimId;
  } else {
    uniqueId = selectedCase?.isManual
      ? selectedCase?.caseId
      : selectedCase?.claimId;
  }

  const getBenefitSelections = (source: any) =>
    source?.benefitSelections || source?.benefit_selections || [];
  const getQuotationLineItems = (source: any) =>
    source?.quotationLineItems ||
    source?.quotation_line_items ||
    source?.lineItems ||
    source?.line_items ||
    [];
  const getLinkedClaimId = (source: any) => source?.claimId || source?.claim_id || "";
  const getLinkedClaimStatus = (source: any) =>
    source?.claimStatus || source?.claim_status || "";
  const isPolicyholderCase = Boolean(
    getLinkedClaimId(selectedCase) ||
    (selectedCase as any)?.funeralPackageSourceType === "POLICY" ||
    (selectedCase as any)?.funeral_package_source_type === "POLICY",
  );
  const canOpenCashConversion = Boolean(
    viewMode !== "BODY_REMOVAL" &&
    viewMode !== "QUOTATIONS_DETAILS" &&
    canConvertCashClient &&
    selectedCase?.isManual &&
    !isPolicyholderCase &&
    !["COMPLETED", "CANCELLED", "CLAIM_DECLINED"].includes(String(selectedCase?.caseStatus || "")),
  );

  const resetConversionDialog = () => {
    setConversionPolicyNumber("");
    setConversionReason("");
    setConversionClaims([]);
    setSelectedConversionClaim(null);
    setConversionError("");
  };

  const searchConversionClaim = async () => {
    const policyNumber = conversionPolicyNumber.trim();
    if (!policyNumber) {
      setConversionError("Enter the policy number first.");
      return;
    }
    setIsSearchingConversionClaim(true);
    setConversionError("");
    setConversionClaims([]);
    setSelectedConversionClaim(null);
    const response: any = await searchClaim(policyNumber, "DEATH");
    setIsSearchingConversionClaim(false);
    if (response?.status !== 200) {
      setConversionError(response?.data?.message || "Claims System is currently unavailable. Please try again.");
      return;
    }
    const exactMatches = (Array.isArray(response.data?.claims) ? response.data.claims : [])
      .filter((claim: any) => String(claim?.policy_number || "").trim().toUpperCase() === policyNumber.toUpperCase());
    if (!exactMatches.length) {
      setConversionError(`No death claim was found for policy ${policyNumber}. Create or verify the claim in the Claims System first.`);
      return;
    }
    setConversionClaims(exactMatches);
    if (exactMatches.length === 1) setSelectedConversionClaim(exactMatches[0]);
  };

  const handleConvertCashClient = async () => {
    if (!selectedCase?.caseId || !selectedConversionClaim?.claim_id || !currentUserId) {
      setConversionError("Select a valid death claim before converting this client.");
      return;
    }
    if (!conversionReason.trim()) {
      setConversionError("Enter a conversion reason for the audit trail.");
      return;
    }
    setIsConvertingClient(true);
    setConversionError("");
    const response: any = await convertCashClientToPolicyholder(selectedCase.caseId, {
      claimId: selectedConversionClaim.claim_id,
      policyNumber: conversionPolicyNumber.trim(),
      conversionReason: conversionReason.trim(),
      performedByUserId: currentUserId,
    });
    setIsConvertingClient(false);
    if (response?.status !== 200) {
      setConversionError(response?.data?.message || "The client could not be converted. Please verify the claim details.");
      return;
    }
    const refreshed: any = await getCaseDetails(selectedCase.caseNumber as string);
    if (refreshed?.status === 200 && refreshed.data) setSelectedCase(refreshed.data);
    setShowConversionDialog(false);
    resetConversionDialog();
    showSuccessToast(response?.data?.message || "Cash client converted to policyholder successfully.");
  };

  useEffect(() => {
    const caseNumber = selectedCase?.caseNumber;
    if (
      viewMode === "BODY_REMOVAL" ||
      !caseNumber ||
      refreshedCaseNumberRef.current === caseNumber
    ) {
      return;
    }

    refreshedCaseNumberRef.current = caseNumber;
    let isMounted = true;
    getCaseDetails(caseNumber)
      .then((response: any) => {
        if (isMounted && response?.status === 200 && response.data) {
          setSelectedCase(response.data);
        }
      })
      .catch((error: any) => {
        console.error("Failed to refresh case details", error);
        refreshedCaseNumberRef.current = null;
      });

    return () => {
      isMounted = false;
    };
  }, [selectedCase?.caseNumber, viewMode, setSelectedCase]);

  const casePortalTabs = [
    {
      id: 0,
      icon: { active: activeResume, inactive: inactiveResume },
      alt: " Informant Details",
    },
    {
      id: 1,
      icon: { active: activeDeceased, inactive: inactiveDeceased },
      alt: " Deceased Details",
    },
    {
      id: 2,
      icon: { active: acticeServices, inactive: inactiveServices },
      alt: " Requested Services",
    },
    {
      id: 3,
      icon: { active: activeLocation, inactive: inactiveLocation },
      alt: " Body Location",
    },
    {
      id: 4,
      icon: { active: activeDocuments, inactive: inactiveDocuments },
      alt: " Documents",
    },
  ];

  const bodyRemovalTabs = [
    {
    id: 0,
    icon: { active: activeVehicle, inactive: inactiveVehicle },
    alt: " Pickup Details",
  },
  {
    id: 1,
    icon: { active: activeDeceased, inactive: inactiveDeceased },
    alt: " Body Removal Form",
  },
    {
      id: 2,
      icon: { active: activeDocuments, inactive: inactiveDocuments },
      alt: " Documents",
    },
  ];

  const quotationTabs = [
    {
      id: 0,
      icon: { active: activeResume, inactive: inactiveResume },
      alt: " Informant Details",
    },
    {
      id: 1,
      icon: { active: activeDeceased, inactive: inactiveDeceased },
      alt: " Deceased Details",
    },
    {
      id: 2,
      icon: { active: acticeServices, inactive: inactiveServices },
      alt: " Requested Services",
    },
    {
      id: 3,
      icon: { active: activeLocation, inactive: inactiveLocation },
      alt: " Body Location",
    },
    {
      id: 4,
      icon: { active: activePricing, inactive: inactivePricing },
      alt: " Package / Pricing",
    },
    {
      id: 5,
      icon: { active: activeDocuments, inactive: inactiveDocuments },
      alt: " Documents",
    },
  ];

  const renderTitle = () => {
    return tabData[activeTab]?.alt || "";
  };

  const tabData =
    viewMode === "BODY_REMOVAL"
      ? bodyRemovalTabs
      : viewMode === "QUOTATIONS_DETAILS"
        ? quotationTabs
        : casePortalTabs;

  const breadCrumb =
    viewMode === "BODY_REMOVAL"
      ? "Body Removal /"
      : viewMode === "QUOTATIONS_DETAILS"
        ? "Quotations /"
        : "Case Portal /";

  const renderTabContent = () => {
    if (viewMode === "BODY_REMOVAL") {
      switch (activeTab) {
         case 0:
    return (
      <PickupDetails
        pickupData={selectedBodyRemoval}
        refreshBodyRemovals={refreshBodyRemovals}
        DriverAndVehicleData={selectedBodyRemoval?.allocationDetails}
      />
    );

  case 1:
    return (
      <BodyRemovalForm
        deceasedData={deceasedData}
        canEditDetails={
          isArrivedAtScene ||
          isDetailsConfirmed ||
          isRemovalOrderCreated ||
          isBodyCollectionCompleted
        }
      />
    );

        case 2:
          return (
            <Documents
              documentData={documentData}
              uniqueId={uniqueId}
              viewMode={viewMode}
            />
          );
        default:
          return null;
      }
    }

    if (viewMode === "QUOTATIONS_DETAILS") {
      switch (activeTab) {
        case 0:
          return <InformantDetails claimantData={claimantData} />;
        case 1:
          return <DeceasedDetails deceasedData={deceasedData} />;
        case 2:
          return (
            <RequestedServices
              isEditable={false}
              caseSource={
                (selectedQuotation as any)?.funeralPackageSourceType ||
                ((selectedQuotation as any)?.claimId ? "POLICY" : "MANUAL")
              }
              benefitSelections={getBenefitSelections(selectedQuotation)}
              lineItems={getQuotationLineItems(selectedQuotation)}
              claimId={getLinkedClaimId(selectedQuotation)}
              claimStatus={getLinkedClaimStatus(selectedQuotation)}
            />
          );
        case 3:
          return <BodyLocation bodyLocationData={bodyLocationData} mortuaryName={mortuaryName} />;
        case 4:
          return <Package lineItems={getQuotationLineItems(selectedQuotation)} />;
        case 5:
          return <Documents documentData={documentData} />;
        default:
          return null;
      }
    }

    // Default to CASE-PORTAL
    switch (activeTab) {
      case 0:
        return (
          <InformantDetails
            claimantData={claimantData}
            isEditable={isEditMode}
            formData={informantDetails}
            onChange={setInformantDetails}
            branchOptions={branchOptions}
          />
        );
      case 1:
        return (
          <DeceasedDetails
            deceasedData={deceasedData}
            isEditable={isEditMode}
            formData={deceasedDetails}
            onChange={setDeceasedDetails}
          />
        );
      case 2:
        return (
          <RequestedServices
            isEditable={isEditMode}
            caseSource={
              (selectedCase as any)?.funeralPackageSourceType ||
              ((selectedCase as any)?.claimId ? "POLICY" : "MANUAL")
            }
            benefitSelections={getBenefitSelections(selectedCase)}
            lineItems={getQuotationLineItems(selectedCase)}
            claimId={getLinkedClaimId(selectedCase)}
            claimStatus={getLinkedClaimStatus(selectedCase)}
            canEditPackage={canWriteIfasCase && isAssignedToMe}
            onPackageSelected={saveFuneralPackageSelection}
          />
        );
      case 3:
        return (
          <BodyLocation
            bodyLocationData={bodyLocationData}
            isEditable={isEditMode}
            formData={deceasedLocation}
            onChange={setDeceasedLocation}
            mortuaryName={mortuaryName}
            onMortuaryChange={setMortuaryName}
            mortuaryOptions={mortuaryOptions}
          />
        );
      case 4:
        return (
          <Documents
            documentData={documentData}
            uniqueId={uniqueId}
            viewMode={viewMode}
          />
        );
      default:
        return null;
    }
  };

  const claimNumber = selectedCase?.isManual
    ? selectedCase?.caseNumber
    : selectedCase?.iceClaimNumber;
  const caseAssignmentDetails =
    selectedCase?.assignedTo ?? (selectedCase as any)?.assigned_to ?? "";
  const assignedPlannerUserId =
    (selectedCase as any)?.assignedToUserId ??
    (selectedCase as any)?.assigned_to_user_id ??
    "";
  const normalizedAssignmentDetails = String(caseAssignmentDetails).trim();
  const isUnassignedCase =
    !normalizedAssignmentDetails ||
    normalizedAssignmentDetails.toLowerCase() === "unassigned" ||
    normalizedAssignmentDetails.toLowerCase() === "null" ||
    normalizedAssignmentDetails.toLowerCase() === "undefined";

  const normalizeIdentity = (value: any) =>
    String(value || "").trim().toLowerCase();

  const assignmentKey = normalizeIdentity(normalizedAssignmentDetails);

  const currentUserKeys = [
    `${first_name || ""} ${last_name || ""}`.trim(),
    first_name,
    last_name,
    user_name,
    email,
    currentUserId,
  ]
    .map(normalizeIdentity)
    .filter(Boolean);
  const isAssignedToMe =
    !isUnassignedCase &&
    (assignedPlannerUserId
      ? normalizeIdentity(assignedPlannerUserId) === normalizeIdentity(currentUserId)
      : currentUserKeys.includes(assignmentKey));
  const canEditCaseDetails = canWriteIfasCase && (
    isAssignedToMe ||
    (isRemovalCaseInitiatorRole(effectiveRole) && isUnassignedCase && !assignedPlannerUserId)
  );

  const documents = documentData || [];
  const bodyRemoval = selectedBodyRemoval as any;
  const bodyRemovalDetails = bodyRemoval?.bodyRemoval || bodyRemoval || {};
  const bodyRemovalCaseAssignmentDetails =
    selectedCase?.assignedTo ??
    (selectedCase as any)?.assigned_to ??
    bodyRemovalDetails?.assignedTo ??
    bodyRemovalDetails?.assigned_to ??
    "";
  const normalizedBodyRemovalAssignmentDetails = String(
    bodyRemovalCaseAssignmentDetails,
  ).trim();
  const isUnassignedBodyRemovalCase =
    !normalizedBodyRemovalAssignmentDetails ||
    normalizedBodyRemovalAssignmentDetails.toLowerCase() === "unassigned" ||
    normalizedBodyRemovalAssignmentDetails.toLowerCase() === "null" ||
    normalizedBodyRemovalAssignmentDetails.toLowerCase() === "undefined";
  const bodyRemovalAssignmentKey = normalizeIdentity(
    normalizedBodyRemovalAssignmentDetails,
  );
  const isBodyRemovalAssignedToMe =
    !isUnassignedBodyRemovalCase &&
    currentUserKeys.includes(bodyRemovalAssignmentKey);
  const allocationDetails = bodyRemoval?.allocationDetails || {};
  const hasText = (value: any) => String(value || "").trim() !== "";
  const getBodyRemovalDetailsBlockMessage = () => {
    const deceased = bodyRemoval?.deceased || bodyRemovalDetails?.deceased || {};
    const deceasedFirstName =
      deceased?.firstName || deceased?.first_name || "";
    const deceasedLastName =
      deceased?.lastName || deceased?.last_name || "";
    const missingFields: string[] = [];

    if (!hasText(bodyRemovalDetails?.funeralFileNumber)) {
      missingFields.push("funeral file number");
    }
    if (!hasText(deceasedFirstName) || !hasText(deceasedLastName)) {
      missingFields.push("deceased name");
    }
    if (!hasText(bodyRemovalDetails?.mortuaryName)) {
      missingFields.push("mortuary name");
    }
    if (!hasText(bodyRemovalDetails?.familyContactPerson)) {
      missingFields.push("family contact person");
    }
    if (!hasText(bodyRemovalDetails?.familyContactNumbers)) {
      missingFields.push("family contact number");
    }
    if (!hasText(bodyRemovalDetails?.relationToDeceased)) {
      missingFields.push("relation to deceased");
    }
    if (!hasText(bodyRemovalDetails?.placeOfDeath)) {
      missingFields.push("place of death");
    }
    if (!hasText(bodyRemovalDetails?.deliveryMortuaryAddress)) {
      missingFields.push("delivery mortuary address");
    }
    if (!hasText(bodyRemovalDetails?.nearestPoliceStation)) {
      missingFields.push("nearest police station");
    }
    if (!hasText(bodyRemovalDetails?.dateOfDeath || deceased?.dateOfDeath || deceased?.date_of_death)) {
      missingFields.push("date of death");
    }
    if (!hasText(bodyRemovalDetails?.timeOfDeath)) {
      missingFields.push("time of death");
    }
    if (!hasText(bodyRemovalDetails?.dateOfRemoval)) {
      missingFields.push("date of removal");
    }
    if (!hasText(bodyRemovalDetails?.bodySize)) {
      missingFields.push("body size");
    }
    if (!hasText(bodyRemovalDetails?.stateOfDecomposition)) {
      missingFields.push("state of decomposition");
    }
    if (
      bodyRemovalDetails?.jewelleryRemovedWithDeceased === undefined ||
      bodyRemovalDetails?.jewelleryRemovedWithDeceased === null
    ) {
      missingFields.push("jewellery removed selection");
    }
    if (
      bodyRemovalDetails?.clothesRemovedWithDeceased === undefined ||
      bodyRemovalDetails?.clothesRemovedWithDeceased === null
    ) {
      missingFields.push("clothes removed selection");
    }
    if (
      bodyRemovalDetails?.blanketsRemovedWithDeceased === undefined ||
      bodyRemovalDetails?.blanketsRemovedWithDeceased === null
    ) {
      missingFields.push("blankets removed selection");
    }
    if (
      bodyRemovalDetails?.injuryDetected === undefined ||
      bodyRemovalDetails?.injuryDetected === null
    ) {
      missingFields.push("injury detected selection");
    }
    if (
      bodyRemovalDetails?.hasIcebolethuPolicy === undefined ||
      bodyRemovalDetails?.hasIcebolethuPolicy === null
    ) {
      missingFields.push("Icebolethu policy selection");
    }
    if (!hasText(bodyRemovalDetails?.timeOutFromMortuary)) {
      missingFields.push("time out from mortuary");
    }
    if (
      bodyRemovalDetails?.kmOutFromMortuary === undefined ||
      bodyRemovalDetails?.kmOutFromMortuary === null ||
      bodyRemovalDetails?.kmOutFromMortuary === ""
    ) {
      missingFields.push("KM out from mortuary");
    }
    if (!hasText(bodyRemovalDetails?.arrivalTimeToFamily)) {
      missingFields.push("arrival time to family");
    }
    if (
      bodyRemovalDetails?.injuryDetected === true &&
      !hasText(bodyRemovalDetails?.typeOfInjury)
    ) {
      missingFields.push("Type of injury");
    }

    if (!missingFields.length) {
      return "";
    }

    return `Complete the body removal form before proceeding: ${missingFields.join(", ")}`;
  };

  const toDateTimeLocalValue = (value?: any) => {
    const date = value ? new Date(value) : new Date();
    const safeDate = Number.isNaN(date.getTime()) ? new Date() : date;
    const pad = (part: number) => String(part).padStart(2, "0");
    return `${safeDate.getFullYear()}-${pad(safeDate.getMonth() + 1)}-${pad(
      safeDate.getDate(),
    )}T${pad(safeDate.getHours())}:${pad(safeDate.getMinutes())}`;
  };

  const normalizeDateTimeForApi = (value: string) => {
    if (!value) return "";
    return value.length === 16 ? `${value}:00` : value;
  };

  const getBodyRemovalDriverId = () =>
    bodyRemoval?.driverId ||
    bodyRemoval?.driverID ||
    bodyRemovalDetails?.driverId ||
    bodyRemovalDetails?.driverID ||
    allocationDetails?.driverId ||
    allocationDetails?.driverID ||
    allocationDetails?.driver?.driverID ||
    "";

  const normalizedAllocatedDriverId = normalizeIdentity(getBodyRemovalDriverId());
  const normalizedAuthDriverId = normalizeIdentity(authDriverId);
  const isDriverUser = isIfasDriverRole(effectiveRole);
  const isAllocatedDriverUser =
    Boolean(normalizedAuthDriverId) &&
    normalizedAuthDriverId === normalizedAllocatedDriverId;
  const canPerformDriverBodyRemovalActions = isDriverUser && isAllocatedDriverUser;

  const bodyRemovalStatus = String(
    bodyRemoval?.bodyRemovalStatus ||
      bodyRemoval?.status ||
      bodyRemoval?.bodyRemoval?.bodyRemovalStatus ||
      bodyRemoval?.bodyRemoval?.status ||
      "",
  ).toUpperCase();
  const isRemovalOrderCreated = bodyRemovalStatus === "REMOVAL_ORDER_CREATED";
  const isDispatchAccepted = bodyRemovalStatus === "DRIVER_ACCEPTED";
  const isEnRoute = bodyRemovalStatus === "EN_ROUTE";
  const isArrivedAtScene = bodyRemovalStatus === "ARRIVED_AT_SCENE";
  const isDetailsConfirmed = bodyRemovalStatus === "DETAILS_CONFIRMED";
  const isBodyDelivered = ["BODY_DELIVERED", "DELIVERED", "COMPLETED"].includes(
    bodyRemovalStatus,
  );
  const isArrivedAtMortuary = ["ARRIVED_AT_MORTUARY"].includes(bodyRemovalStatus);
  const isBodyCollectionCompleted = [
    "BODY_COLLECTED",
    "COLLECTED",
    "ARRIVED_AT_MORTUARY",
    "BODY_DELIVERED",
    "DELIVERED",
    "COMPLETED",
  ].includes(bodyRemovalStatus);
  const bodyRemovalDetailsBlockMessage = getBodyRemovalDetailsBlockMessage();
  const isBodyRemovalDetailsComplete = !bodyRemovalDetailsBlockMessage;
  const canOpenBodyRemovalForm =
    canPerformDriverBodyRemovalActions &&
    isArrivedAtScene;
  const canAcceptDispatch =
    canPerformDriverBodyRemovalActions &&
    bodyRemovalStatus === "ALLOCATED";
  const canMarkEnRoute = canPerformDriverBodyRemovalActions && isDispatchAccepted;
  const canMarkArrivedAtScene = canPerformDriverBodyRemovalActions && isEnRoute;
  const canCreateRemovalOrder =
    canPerformDriverBodyRemovalActions &&
    (isArrivedAtScene || isDetailsConfirmed);
  const canConfirmBodyCollection =
    canPerformDriverBodyRemovalActions && isRemovalOrderCreated;
  const canMarkArrivedAtMortuary =
    canPerformDriverBodyRemovalActions && bodyRemovalStatus === "BODY_COLLECTED";
  const canDeliverBody =
    canPerformDriverBodyRemovalActions &&
    isBodyDelivered === false &&
    isArrivedAtMortuary;

  const isBodyRemovalActionLoading =
    isCreatingRemovalOrder ||
    isConfirmingBodyCollection;
  const bodyRemovalActionLabel = isBodyRemovalActionLoading
    ? "Processing..."
    : canAcceptDispatch
      ? "Accept Dispatch"
      : canMarkEnRoute
        ? "Mark En Route"
        : canMarkArrivedAtScene
          ? "Arrived At Scene"
          : canOpenBodyRemovalForm
            ? "Complete Body Removal Details"
            : canCreateRemovalOrder
              ? "Create Order"
              : canConfirmBodyCollection
                ? "Confirm Body Collection"
                : canMarkArrivedAtMortuary
                  ? "Arrived At Mortuary"
                  : "Awaiting Next Step";

  const ensureAllocatedDriverAccess = () => {
    if (!isDriverUser) {
      showInfoToast("Only the assigned driver can perform this body removal action");
      return false;
    }

    if (!normalizedAuthDriverId) {
      showInfoToast("No driver profile is linked to this login yet");
      return false;
    }

    if (!isAllocatedDriverUser) {
      showInfoToast("This body removal is assigned to a different driver");
      return false;
    }

    return true;
  };

  const refreshSelectedBodyRemovalDetails = async (bodyRemovalNumber: string) => {
    const refreshedBodyRemoval = await getBodyRemovalDetails(bodyRemovalNumber);
    if (refreshedBodyRemoval?.status === 200) {
      setSelectedBodyRemoval(refreshedBodyRemoval.data);
    }
  };

  const hydrateEditableCase = () => {
    if (!selectedCase) {
      return;
    }

    const claimant = selectedCase.claimant as any;
    const deceased = selectedCase.deceased as any;
    const bodyRemoval = selectedCase.bodyRemoval as any;
    const savedLocation = (selectedCase as any).deceasedLocation || bodyRemoval || {};
    const value = (source: any, camel: string, snake: string) =>
      source?.[camel] ?? source?.[snake] ?? "";

    setInformantDetails({
      title: claimant?.title || "",
      initials: claimant?.initials || "",
      firstName: value(claimant, "firstName", "first_name"),
      lastName: value(claimant, "lastName", "last_name"),
      dateOfBirth: value(claimant, "dateOfBirth", "date_of_birth"),
      gender: claimant?.gender || "",
      maritalStatus: value(claimant, "maritalStatus", "marital_status"),
      nationality: claimant?.nationality || "",
      countryOfBirth: value(claimant, "countryOfBirth", "country_of_birth"),
      countryOfResidence: value(claimant, "countryOfResidence", "country_of_residence"),
      beneficiaryEmployer: value(claimant, "beneficiaryEmployer", "beneficiary_employer"),
      agentOrBroker: value(claimant, "agentOrBroker", "agent_or_broker"),
      address: claimant?.address || "",
      contactNumber: value(claimant, "contactNumber", "contact_number"),
      email: claimant?.email || "",
      branchName: value(claimant, "branchName", "branch_name"),
      postalAddress: value(claimant, "postalAddress", "postal_address"),
      occupation: claimant?.occupation || "",
      sourceOfFunds: value(claimant, "sourceOfFunds", "source_of_funds"),
      identificationNumber: value(claimant, "identificationNumber", "identification_number"),
      postalCode: value(claimant, "postalCode", "postal_code"),
      identificationType: value(claimant, "identificationType", "identification_type"),
      networkProvider: value(claimant, "networkProvider", "network_provider"),
      relationship: claimant?.relationship || "",
    });

    setDeceasedDetails({
      title: deceased?.title || "",
      initials: deceased?.initials || "",
      firstName: value(deceased, "firstName", "first_name"),
      lastName: value(deceased, "lastName", "last_name"),
      gender: deceased?.gender || "",
      relationship: deceased?.relationship || "",
      identificationNumber: value(deceased, "identificationNumber", "identification_number"),
      identificationType: value(deceased, "identificationType", "identification_type"),
      email: deceased?.email || "",
      contactNumber: value(deceased, "contactNumber", "contact_number"),
      dateOfDeath: value(deceased, "dateOfDeath", "date_of_death"),
      dateOfBirth: value(deceased, "dateOfBirth", "date_of_birth"),
      causeOfDeath: value(deceased, "causeOfDeath", "cause_of_death"),
    });

    setDeceasedLocation({
        houseNumber: value(savedLocation, "houseNumber", "house_number"),
        streetName: value(savedLocation, "streetName", "street_name"),
        suburb: savedLocation?.suburb || "",
        city: savedLocation?.city || "",
        province: savedLocation?.province || "",
        postalCode: value(savedLocation, "postalCode", "postal_code"),
        notes: savedLocation?.notes || savedLocation?.remarks || "",
      });
    setMortuaryName(
      (selectedCase as any)?.mortuaryName ??
      (selectedCase as any)?.mortuary_name ??
      "",
    );

    const packageDetails = (selectedCase as any)?.funeralPackageSelection || {};
    setPackageSelection({
      sourceType:
        packageDetails?.sourceType ||
        (selectedCase as any)?.funeralPackageSourceType ||
        ((selectedCase as any)?.claimId ? "POLICY" : "MANUAL"),
      funeralCategoryCode: packageDetails?.categoryCode || "",
      funeralCategoryName: packageDetails?.categoryName || "",
      funeralPackageCode: packageDetails?.packageCode || "",
      funeralPackageName: packageDetails?.packageName || "",
      funeralPackageVersion: packageDetails?.version || undefined,
      summary: packageDetails?.summary || "",
      sections: packageDetails?.sections || [],
    });
    };

  const appendDocumentField = async (
    formData: FormData,
    fieldName: string,
    localDocumentKeys: string[],
    documentTypes: string[],
    appendEmpty = false,
  ) => {
    const localDocumentKey = localDocumentKeys.find(
      (key) => localDocuments[key as keyof typeof localDocuments],
    );

    if (localDocumentKey) {
      const localFile = localDocuments[
        localDocumentKey as keyof typeof localDocuments
      ] as File;
      formData.append(fieldName, localFile);
      return;
    }

    const existingDocument = documents.find((document: any) =>
      documentTypes.includes(document.document_type),
    );

    if (!existingDocument || !claimNumber) {
      if (appendEmpty) {
        formData.append(fieldName, "");
      }
      return;
    }

    const response = await getDocument(claimNumber, existingDocument.document_id);

    if (response?.status === 200) {
      formData.append(
        fieldName,
        response.data,
        existingDocument.file_name || `${fieldName}.pdf`,
      );
      return;
    }

    if (appendEmpty) {
      formData.append(fieldName, "");
    }
  };

  const handleSubmitCase = async () => {
    if (!canSubmitIfasCase) {
      showInfoToast("Your role cannot submit this case.");
      return;
    }
    if (viewMode === "BODY_REMOVAL" || viewMode === "QUOTATIONS_DETAILS") {
      return;
    }

    if (!selectedCase?.caseId) {
      showInfoToast("Unable to identify the selected case");
      return;
    }

    if (!currentUserId) {
      showInfoToast("Unable to identify the current user");
      return;
    }

    if (!String(mortuaryName || "").trim()) {
      showInfoToast("Select a mortuary before submitting the case");
      return;
    }

    try {
      setIsSubmitting(true);

      const formData = new FormData();
      const caseBlob = new Blob(
        [
          JSON.stringify({
            case_id: selectedCase.caseId,
            user_id: currentUserId,
            mortuary_name: mortuaryName || undefined,
          }),
        ],
        { type: "application/json" },
      );

      formData.append("case", caseBlob);

      await appendDocumentField(formData, "death_certificate", ["death_certificate"], ["DEATH_CERTIFICATE"]);
      await appendDocumentField(formData, "claimant_id", ["claimant_id"], ["CLAIMANT_ID"]);
      await appendDocumentField(formData, "deceased_id", ["deceased_id"], ["DECEASED_ID"]);
      await appendDocumentField(formData, "dha_1663_form", ["dha_1663_form"], ["DHA_1663_FORM"]);
      await appendDocumentField(formData, "dha_1680_home_death_form", ["dha_1680_home_death_form"], ["DHA_1680_HOME_DEATH_FORM"]);
      await appendDocumentField(formData, "claimant_signature", ["signature_of_claimant"], ["SIGNATURE_OF_CLAIMANT"]);
      await appendDocumentField(formData, "bank_statement", ["bank_statement"], ["BANK_STATEMENT"]);

      const response = await submitCase(formData);

      if (response?.status === 200 || response?.status === 201) {
        resetDocuments();
        setShouldResetAfterSubmit(true);
        setVerifiedLabel("Submitted");
        setShowVerified(true);
      } else {
        showInfoToast(
          response?.data?.message ||
          response?.response?.data?.message ||
          response?.message ||
          "Unable to submit case",
        );
      }
    } catch (error: any) {
      console.error("Case submission failed", error);
      showInfoToast(
        error?.response?.data?.message ||
        error?.data?.message ||
        error?.message ||
        "Unable to submit case",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const buildCaseUpdatePayload = (packageOverride?: {
    categoryCode: string;
    packageCode: string;
    quotationLineItems?: Array<{ quotableItemCode: string; quantity: number; unitPrice: number }>;
  }) => ({
    caseId: selectedCase?.caseId,
    userId: currentUserId,
    claimant: {
      beneficiaryEmployer: informantDetails.beneficiaryEmployer || "",
      agentOrBroker: informantDetails.agentOrBroker || "",
      lastName: informantDetails.lastName || "",
      gender: informantDetails.gender || "",
      maritalStatus: informantDetails.maritalStatus || "",
      identificationType: informantDetails.identificationType || "",
      postalAddress: informantDetails.postalAddress || "",
      contactNumber: informantDetails.contactNumber || "",
      occupation: informantDetails.occupation || "",
      branchName: informantDetails.branchName || "",
      initials: informantDetails.initials || "",
      countryOfBirth: informantDetails.countryOfBirth || "",
      address: informantDetails.address || "",
      identificationNumber: informantDetails.identificationNumber || "",
      postalCode: informantDetails.postalCode || "",
      relationship: informantDetails.relationship || "",
      title: informantDetails.title || "",
      countryOfResidence: informantDetails.countryOfResidence || "",
      dateOfBirth: informantDetails.dateOfBirth || "",
      firstName: informantDetails.firstName || "",
      nationality: informantDetails.nationality || "",
      sourceOfFunds: informantDetails.sourceOfFunds || "",
      email: informantDetails.email || "",
      networkProvider: informantDetails.networkProvider || "",
    },
    deceased: {
      lastName: deceasedDetails.lastName || "",
      gender: deceasedDetails.gender || "",
      identificationType: deceasedDetails.identificationType || "",
      email: deceasedDetails.email || "",
      contactNumber: deceasedDetails.contactNumber || "",
      memberCard: "",
      initials: deceasedDetails.initials || "",
      dateOfDeath: deceasedDetails.dateOfDeath || "",
      stillBorn: false,
      causeOfDeath: deceasedDetails.causeOfDeath || "",
      isNaturalDeath: "",
      identificationNumber: deceasedDetails.identificationNumber || "",
      relationship: deceasedDetails.relationship || "",
      title: deceasedDetails.title || "",
      dateOfBirth: deceasedDetails.dateOfBirth || "",
      firstName: deceasedDetails.firstName || "",
      passportNumber: "",
    },
      deceasedLocation: {
        houseNumber: deceasedLocation.houseNumber || "",
        streetName: deceasedLocation.streetName || "",
      suburb: deceasedLocation.suburb || "",
      city: deceasedLocation.city || "",
        province: deceasedLocation.province || "",
        postalCode: deceasedLocation.postalCode || "",
        notes: deceasedLocation.notes || "",
      },
      mortuaryName: mortuaryName || "",
      funeralCategoryCode: packageOverride?.categoryCode || packageSelection.funeralCategoryCode || "",
      funeralPackageCode: packageOverride?.packageCode || packageSelection.funeralPackageCode || "",
      quotationLineItems: packageOverride?.quotationLineItems,
    });

  const saveFuneralPackageSelection = async (
    categoryCode: string,
    packageCode: string,
    quotationLineItems: Array<{ quotableItemCode: string; quantity: number; unitPrice: number }>,
  ) => {
    if (!selectedCase?.caseId || !currentUserId) {
      showInfoToast("Unable to save the requested services for this case");
      return;
    }
    try {
      const formData = new FormData();
      formData.append("case", JSON.stringify(buildCaseUpdatePayload({
        categoryCode,
        packageCode,
        quotationLineItems,
      })));
      const response = await updateCase(formData);
      if (response?.status === 200) {
        if (selectedCase.caseNumber) {
          const refreshedCase = await getCaseDetails(selectedCase.caseNumber);
          if (refreshedCase?.status === 200) setSelectedCase(refreshedCase.data);
        }
        showSuccessToast("Requested services saved");
      } else {
        showInfoToast(response?.data?.message || "Unable to save requested services");
      }
    } catch (error: any) {
      showInfoToast(error?.response?.data?.message || "Unable to save requested services");
    }
  };

  const handleUpdateCase = async () => {
    if (!canWriteIfasCase) {
      showInfoToast("Your role cannot update this case.");
      return;
    }
    if (!selectedCase?.caseId) {
      showInfoToast("Unable to identify the selected case");
      return;
    }

    const todayLocal = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
      .toISOString()
      .split("T")[0];

    if (deceasedDetails?.dateOfBirth && deceasedDetails.dateOfBirth > todayLocal) {
      showInfoToast("Date of birth cannot be in the future");
      return;
    }

    if (deceasedDetails?.dateOfDeath && deceasedDetails.dateOfDeath > todayLocal) {
      showInfoToast("Date of death cannot be in the future");
      return;
    }

    try {
      setIsSubmitting(true);

      const formData = new FormData();
      formData.append("case", JSON.stringify(buildCaseUpdatePayload()));

      await appendDocumentField(formData, "death_certificate", ["death_certificate"], ["DEATH_CERTIFICATE"], true);
      await appendDocumentField(formData, "claimant_id", ["claimant_id"], ["CLAIMANT_ID"], true);
      await appendDocumentField(formData, "deceased_id", ["deceased_id"], ["DECEASED_ID"], true);
      await appendDocumentField(formData, "dha_forms", ["dha_1663_form", "dha_1680_home_death_form"], ["DHA_1663_FORM", "DHA_1680_HOME_DEATH_FORM"], true);
      await appendDocumentField(formData, "signature_of_claimant", ["signature_of_claimant"], ["SIGNATURE_OF_CLAIMANT"], true);
      await appendDocumentField(formData, "bank_statement", ["bank_statement"], ["BANK_STATEMENT"], true);

      const response = await updateCase(formData);

      if (response?.status === 200 || response?.status === 201) {
        const refreshedCase = await getCaseDetails(selectedCase.caseNumber || "");
        if (refreshedCase?.status === 200) {
          setSelectedCase(refreshedCase.data);
        }
        setShouldResetAfterSubmit(false);
        setVerifiedLabel("Updated");
        setShowVerified(true);
        setIsEditMode(false);
      } else {
        showInfoToast(response?.data?.message || "Unable to update case");
      }
    } catch (error) {
      console.error("Case update failed", error);
      showInfoToast("Unable to update case");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateRemovalOrder = async () => {
    if (viewMode !== "BODY_REMOVAL") {
      return;
    }
    if (!ensureAllocatedDriverAccess()) {
      return;
    }
    const bodyRemovalNumber = selectedBodyRemoval?.bodyRemovalNumber;

    if (!bodyRemovalNumber) {
      showInfoToast("Unable to identify the selected body removal");
      return;
    }

    try {
      setIsCreatingRemovalOrder(true);

      const response = await createRemovalOrder(bodyRemovalNumber, {
        driverId: authDriverId,
        createdBy: role,
        notes: "",
      });

      if (response?.status === 200 || response?.status === 201) {
        setVerifiedLabel("Removal Order Created");
        setShowVerified(true);
        setShouldResetToBodyRemovalList(true);
      } else {
        showInfoToast(response?.data?.message || "Unable to create removal order");
      }
    } catch (error) {
      console.error("Removal order creation failed", error);
      showInfoToast("Unable to create removal order");
    } finally {
      setIsCreatingRemovalOrder(false);
    }
  };

  const handleAcceptDispatch = async () => {
    if (viewMode !== "BODY_REMOVAL") {
      return;
    }
    if (!ensureAllocatedDriverAccess()) {
      return;
    }

    const bodyRemovalNumber = selectedBodyRemoval?.bodyRemovalNumber;

    if (!bodyRemovalNumber) {
      showInfoToast("Unable to identify the selected body removal");
      return;
    }

    try {
      setIsCreatingRemovalOrder(true);

      const response = await acceptBodyRemovalDispatch(bodyRemovalNumber, {
        driverId: authDriverId,
        actionDateTime: new Date().toISOString(),
        updatedBy: role,
        notes: "",
      });

      if (response?.status === 200 || response?.status === 201) {
        setVerifiedLabel("Dispatch Accepted");
        setShowVerified(true);
        setShouldResetToBodyRemovalList(true);
      } else {
        showInfoToast(response?.data?.message || "Unable to accept dispatch");
      }
    } catch (error) {
      console.error("Dispatch acceptance failed", error);
      showInfoToast("Unable to accept dispatch");
    } finally {
      setIsCreatingRemovalOrder(false);
    }
  };

  const handleMarkEnRoute = async () => {
    if (viewMode !== "BODY_REMOVAL") {
      return;
    }
    if (!ensureAllocatedDriverAccess()) {
      return;
    }

    const bodyRemovalNumber = selectedBodyRemoval?.bodyRemovalNumber;

    if (!bodyRemovalNumber) {
      showInfoToast("Unable to identify the selected body removal");
      return;
    }

    try {
      setIsCreatingRemovalOrder(true);

      const response = await markBodyRemovalEnRoute(bodyRemovalNumber, {
        driverId: authDriverId,
        actionDateTime: new Date().toISOString(),
        updatedBy: role,
        notes: "",
      });

      if (response?.status === 200 || response?.status === 201) {
        setVerifiedLabel("Marked En Route");
        setShowVerified(true);
        setShouldResetToBodyRemovalList(true);
      } else {
        showInfoToast(response?.data?.message || "Unable to mark en route");
      }
    } catch (error) {
      console.error("En route update failed", error);
      showInfoToast("Unable to mark en route");
    } finally {
      setIsCreatingRemovalOrder(false);
    }
  };

  const handleArrivedAtScene = async () => {
    if (viewMode !== "BODY_REMOVAL") {
      return;
    }
    if (!ensureAllocatedDriverAccess()) {
      return;
    }

    const bodyRemovalNumber = selectedBodyRemoval?.bodyRemovalNumber;

    if (!bodyRemovalNumber) {
      showInfoToast("Unable to identify the selected body removal");
      return;
    }

    try {
      setIsCreatingRemovalOrder(true);

      const response = await markBodyRemovalArrivedAtScene(bodyRemovalNumber, {
        driverId: authDriverId,
        actionDateTime: new Date().toISOString(),
        updatedBy: role,
        notes: "",
      });

      if (response?.status === 200 || response?.status === 201) {
        setVerifiedLabel("Arrived At Scene");
        setShowVerified(true);
        setShouldResetToBodyRemovalList(true);
      } else {
        showInfoToast(response?.data?.message || "Unable to confirm arrival at scene");
      }
    } catch (error) {
      console.error("Arrival at scene update failed", error);
      showInfoToast("Unable to confirm arrival at scene");
    } finally {
      setIsCreatingRemovalOrder(false);
    }
  };

  const handleConfirmBodyCollection = async () => {
    if (viewMode !== "BODY_REMOVAL") {
      return;
    }
    if (!ensureAllocatedDriverAccess()) {
      return;
    }

    const bodyRemovalNumber = selectedBodyRemoval?.bodyRemovalNumber;

    if (!bodyRemovalNumber) {
      showInfoToast("Unable to identify the selected body removal");
      return;
    }

    try {
      setIsConfirmingBodyCollection(true);

      const response = await confirmBodyCollection(bodyRemovalNumber, {
        driverId: authDriverId,
        collectionDateTime: new Date().toISOString(),
        notes: "",
      });

      if (response?.status === 200 || response?.status === 201) {
        setVerifiedLabel("Body Collection Confirmed");
        setShowVerified(true);
        setShouldResetToBodyRemovalList(true);
      } else {
        showInfoToast(response?.data?.message || "Unable to confirm body collection");
      }
    } catch (error) {
      console.error("Body collection confirmation failed", error);
      showInfoToast("Unable to confirm body collection");
    } finally {
      setIsConfirmingBodyCollection(false);
    }
  };

  const handleArrivedAtMortuary = async () => {
    if (viewMode !== "BODY_REMOVAL") {
      return;
    }
    if (!ensureAllocatedDriverAccess()) {
      return;
    }

    const bodyRemovalNumber = selectedBodyRemoval?.bodyRemovalNumber;

    if (!bodyRemovalNumber) {
      showInfoToast("Unable to identify the selected body removal");
      return;
    }

    try {
      setIsConfirmingBodyCollection(true);

      const response = await markBodyRemovalArrivedAtMortuary(bodyRemovalNumber, {
        driverId: authDriverId,
        actionDateTime: new Date().toISOString(),
        updatedBy: role,
        notes: "",
      });

      if (response?.status === 200 || response?.status === 201) {
        setVerifiedLabel("Arrived At Mortuary");
        setShowVerified(true);
        setShouldResetToBodyRemovalList(true);
      } else {
        showInfoToast(response?.data?.message || "Unable to confirm arrival at mortuary");
      }
    } catch (error) {
      console.error("Arrival at mortuary update failed", error);
      showInfoToast("Unable to confirm arrival at mortuary");
    } finally {
      setIsConfirmingBodyCollection(false);
    }
  };

 const handleOpenDeliveryDialog = () => {
  if (viewMode !== "BODY_REMOVAL") {
    return;
  }
  if (!ensureAllocatedDriverAccess()) {
    return;
  }

  const bodyRemovalNumber = selectedBodyRemoval?.bodyRemovalNumber;

    if (!bodyRemovalNumber) {
      showInfoToast("Unable to identify the selected body removal");
      return;
    }

    if (bodyRemovalDetailsBlockMessage) {
      showInfoToast(bodyRemovalDetailsBlockMessage);
      return;
    }

   setDeliveryDetails({
    driverId: authDriverId || "",
    receivedBy:
      bodyRemovalDetails?.mortuaryReceivedBy ||
      bodyRemovalDetails?.receivedBy ||
      bodyRemovalDetails?.received_by ||
      "",
    kmInArrivalAtMortuary:
      bodyRemovalDetails?.kmInArrivalAtMortuary === undefined ||
      bodyRemovalDetails?.kmInArrivalAtMortuary === null
        ? ""
        : String(bodyRemovalDetails.kmInArrivalAtMortuary),
    remarks:
      bodyRemovalDetails?.mortuaryRemarks ||
      bodyRemovalDetails?.bodyDeliveryNotes ||
      bodyRemovalDetails?.remarks ||
      "",
  });

  setShowDeliveryDialog(true);
};

  const handleDeliveryFieldChange =
    (field: keyof typeof deliveryDetails) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setDeliveryDetails((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));
    };

  const handleDeliverBody = async () => {
    if (viewMode !== "BODY_REMOVAL") {
      return;
    }
    if (!ensureAllocatedDriverAccess()) {
      return;
    }

    const bodyRemovalNumber = selectedBodyRemoval?.bodyRemovalNumber;

    if (!bodyRemovalNumber) {
      showInfoToast("Unable to identify the selected body removal");
      return;
    }

    if (!deliveryDetails.driverId) {
      showInfoToast("Driver is required before delivery");
      return;
    }

    if (!deliveryDetails.receivedBy.trim()) {
      showInfoToast("Received by is required");
      return;
    }

    if (deliveryDetails.kmInArrivalAtMortuary === "") {
      showInfoToast("KM in arrival at mortuary is required");
      return;
    }

    if (!deliveryDetails.remarks.trim()) {
  showInfoToast("Handover Remarks is required");
  return;
}

    const kmInArrivalAtMortuary = Number(deliveryDetails.kmInArrivalAtMortuary);
    if (!Number.isFinite(kmInArrivalAtMortuary) || kmInArrivalAtMortuary < 0) {
      showInfoToast("KM in arrival at mortuary must be a valid number");
      return;
    }

    const kmOutFromMortuary = Number(bodyRemovalDetails?.kmOutFromMortuary);
    if (
      Number.isFinite(kmOutFromMortuary) &&
      kmInArrivalAtMortuary < kmOutFromMortuary
    ) {
      showInfoToast("KM in cannot be less than KM out from mortuary");
      return;
    }

    try {
      setIsDelivering(true);

 const payload = {
  driverId: authDriverId,
  receivedBy: deliveryDetails.receivedBy.trim(),
  kmInArrivalAtMortuary,
  remarks: deliveryDetails.remarks.trim(),
};

      const response = await deliverBodyRemoval(bodyRemovalNumber, payload);

      if (response?.status === 200 || response?.status === 201) {
        const refreshedBodyRemoval = await getBodyRemovalDetails(bodyRemovalNumber);
        if (refreshedBodyRemoval?.status === 200) {
          setSelectedBodyRemoval(refreshedBodyRemoval.data);
        }
        setVerifiedLabel("Body Handover Completed");
        setShowVerified(true);
        setShowDeliveryDialog(false);
        setShouldResetToBodyRemovalList(true);
      } else {
        showInfoToast(response?.data?.message || "Unable to complete handover");
      }
    } catch (error) {
      console.error("Body handover failed", error);
      showInfoToast("Unable to complete handover");
    } finally {
      setIsDelivering(false);
    }
  };

  const handleBodyRemovalAction = () => {
    if (canAcceptDispatch) {
      handleAcceptDispatch();
      return;
    }

    if (canMarkEnRoute) {
      handleMarkEnRoute();
      return;
    }

    if (canMarkArrivedAtScene) {
      handleArrivedAtScene();
      return;
    }

    if (canCreateRemovalOrder) {
      handleCreateRemovalOrder();
      return;
    }

    if (canConfirmBodyCollection) {
      handleConfirmBodyCollection();
      return;
    }

    if (canMarkArrivedAtMortuary) {
      handleArrivedAtMortuary();
    }
  };

  useEffect(() => {
    setActiveTab(0);
    setIsEditMode(false);
  }, [viewMode]);

  useEffect(() => {
    if (viewMode === "BODY_REMOVAL" || viewMode === "QUOTATIONS_DETAILS") {
      return;
    }
    resetDocuments();
    hydrateEditableCase();
  }, [selectedCase?.caseId, viewMode, resetDocuments]);

  useEffect(() => {
    if (!showVerified) return;
    const timer = setTimeout(() => {
      setShowVerified(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, [showVerified, setShowVerified]);

  useEffect(() => {
    if (!shouldResetAfterSubmit) return;
    const timer = setTimeout(() => {
      setShouldResetAfterSubmit(false);
      setShowVerified(false);
      resetToCaseList();
    }, 2000);
    return () => clearTimeout(timer);
  }, [shouldResetAfterSubmit, resetToCaseList, setShowVerified]);

  useEffect(() => {
    if (!shouldResetToBodyRemovalList) return;
    const timer = setTimeout(() => {
      setShouldResetToBodyRemovalList(false);
      setShowVerified(false);
      resetToBodyRemovalList?.();
    }, 2000);
    return () => clearTimeout(timer);
  }, [shouldResetToBodyRemovalList, resetToBodyRemovalList, setShowVerified]);

  return (
    <Box
      p={{ xs: 1.5, md: 2 }}
      bgcolor="#F6F7F9"
      minHeight={{ xs: "auto", md: "100vh" }}
      sx={{ boxSizing: "border-box" }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexDirection={{ xs: "column", sm: "row" }}
        gap={2}
        mb={2}
        sx={{
          p: { xs: 1.75, md: 2 },
          borderRadius: 2,
          bgcolor: "#FFFFFF",
          border: "1px solid #E6E2DC",
          boxShadow: "0 10px 26px rgba(20, 24, 31, 0.06)",
        }}
      >
        <Typography
          fontSize={{ xs: 22, md: 28 }}
          fontWeight={900}
          color="#181C27"
          lineHeight={1.2}
        >
          <span style={{ color: "#EC6E00", cursor: "pointer" }}>
            {breadCrumb}
          </span>
          {renderTitle()}
        </Typography>

        {/* Body removal step-by-step actions */}
        {viewMode === "BODY_REMOVAL" && (
          <Box
            display="flex"
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="flex-end"
            flexWrap="wrap"
            gap={1.5}
          >
            {!isUnassignedBodyRemovalCase && !isBodyRemovalAssignedToMe && (
              <Chip
                label={`Assigned to ${normalizedBodyRemovalAssignmentDetails}`}
                sx={{
                  bgcolor: "#FFF4EB",
                  color: "#B54708",
                  fontWeight: 800,
                  height: 40,
                }}
              />
            )}

            <ButtonComp
              variant="contained"
              height={40}
              width={120}
              textTransform="none"
              color="secondary"
              onClick={onBodyRemovalHistoryClicked}
              sx={{
        borderRadius: "8px",
        fontWeight: 600,
        boxShadow: "none",
        '&:hover': {
          boxShadow: "none",
          backgroundColor: "#EC6E00"
        }
      }}
            >
              History
            </ButtonComp>

            {!isBodyDelivered && !canDeliverBody && bodyRemovalActionLabel !== "Awaiting Next Step" && (
              <ButtonComp
                variant="contained"
                height={44}
                width={300}
                textTransform="none"
                onClick={handleBodyRemovalAction}
                disabled={isBodyRemovalActionLoading}
             sx={{
      bgcolor: "#EC6E00",  // Same color for all buttons
      borderRadius: "8px",
      fontWeight: 600,
      fontSize: "14px",
      '&:hover': {
        bgcolor: "#d96000",  // Darker on hover
      }
    }}
  
              >
                {bodyRemovalActionLabel}
              </ButtonComp>
            )}

            {canDeliverBody && (
              <ButtonComp
                variant="contained"
                height={44}
                width={200}
                textTransform="none"
                onClick={handleOpenDeliveryDialog}
                disabled={isDelivering}
               sx={{
          bgcolor: "#EC6E00",
          borderRadius: "8px",
          fontWeight: 600,
          fontSize: "14px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          '&:hover': {
            bgcolor: "#d96000",
            transform: "translateY(-1px)",
            boxShadow: "0 4px 8px rgba(0,0,0,0.1)"
          },
          transition: "all 0.2s ease"
        }}
      >
                Complete Handover
              </ButtonComp>
            )}
          </Box>
        )}

        {viewMode !== "BODY_REMOVAL" && viewMode !== "QUOTATIONS_DETAILS" && (
          <Box display="flex" flexDirection="row" flexWrap="wrap" justifyContent="flex-end" gap={1.5}>
            {canOpenCashConversion && (
              <Button
                variant="outlined"
                onClick={() => setShowConversionDialog(true)}
                sx={{
                  height: 40,
                  borderRadius: "8px",
                  px: 2.25,
                  textTransform: "none",
                  fontWeight: 800,
                  color: "#C45700",
                  bgcolor: "#FFF7ED",
                  border: "1px solid #EC6E00",
                  whiteSpace: "nowrap",
                  boxShadow: "none",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    color: "#A94700",
                    border: "1px solid #C45700",
                    bgcolor: "#FFEDD5",
                    boxShadow: "0 2px 6px rgba(236, 110, 0, 0.16)",
                  },
                }}
              >
                Convert to Policyholder
              </Button>
            )}
            {!canEditCaseDetails ? (
              <>
                <ButtonComp
                  variant="contained"
                  height={40}
                  width={120}
                  textTransform="none"
                  color="secondary"
                  onClick={onCaseHistoryClicked}
                >
                  History
                </ButtonComp>
              </>
            ) : !isEditMode ? (
              <>
                <ButtonComp
                  variant="contained"
                  height={40}
                  width={120}
                  textTransform="none"
                  color="secondary"
                  onClick={onCaseHistoryClicked}
                >
                  History
                </ButtonComp>
                {canSubmitIfasCase && (
                  <ButtonComp
                    variant="outlined"
                    height={40}
                    width={190}
                    textTransform="none"
                    onClick={handleSubmitCase}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Submitting..." : "Submit"}
                  </ButtonComp>
                )}
                {canEditCaseDetails && (
                  <BorderColorIcon
                    sx={{ cursor: "pointer", color: "black", fontSize: "2rem" }}
                    onClick={() => {
                      hydrateEditableCase();
                      setIsEditMode(true);
                    }}
                  />
                )}
              </>
            ) : (
              <>
                <ButtonComp
                  variant="outlined"
                  color="secondary"
                  height={40}
                  width={120}
                  textTransform="none"
                  onClick={() => {
                    hydrateEditableCase();
                    resetDocuments();
                    setIsEditMode(false);
                  }}
                >
                  Cancel
                </ButtonComp>
                <ButtonComp
                  variant="contained"
                  height={40}
                  width={120}
                  textTransform="none"
                  color="secondary"
                  onClick={onCaseHistoryClicked}
                >
                  History
                </ButtonComp>
                {canEditCaseDetails && (
                  <ButtonComp
                    variant="contained"
                    height={40}
                    width={190}
                    textTransform="none"
                    onClick={handleUpdateCase}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Updating..." : "Update"}
                  </ButtonComp>
                )}
              </>
            )}
          </Box>
        )}
      </Box>

      {/* Card Container */}
      <Paper
        sx={operationsDetailPaperSx}
        elevation={0}
      >
        <ClaimTabs
          tabs={tabData}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        <Box
          mt={2}
          display="flex"
          flexWrap="wrap"
          gap={1}
          alignItems="center"
        >
          <Chip
            label={claimNumber || "Case"}
            size="small"
            sx={{
              bgcolor: "#FFF4EB",
              color: "primary.main",
              fontWeight: 800,
            }}
          />
          <Chip
  label={
    viewMode === "BODY_REMOVAL"
      ? ((selectedBodyRemoval as any)?.funeralPackageSourceType === "POLICY" || getLinkedClaimId(selectedBodyRemoval))
        ? "Policyholder"
        : "Cash Client"
      : isPolicyholderCase
        ? "Policyholder"
        : "Cash Client"
  }
  size="small"
  sx={{
    bgcolor:
      viewMode === "BODY_REMOVAL"
        ? ((selectedBodyRemoval as any)?.funeralPackageSourceType === "POLICY" || getLinkedClaimId(selectedBodyRemoval))
          ? "#EEF2FF"
          : "#FFF7ED"
        : isPolicyholderCase ? "#EEF2FF" : "#FFF7ED",

    color:
      viewMode === "BODY_REMOVAL"
        ? ((selectedBodyRemoval as any)?.funeralPackageSourceType === "POLICY" || getLinkedClaimId(selectedBodyRemoval))
          ? "#3730A3"
          : "#C2410C"
        : isPolicyholderCase ? "#3730A3" : "#C2410C",

    fontWeight: 800,
  }}
/>
        </Box>

        <Box
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          gap={1.5}
          mt={3}
        >
          {viewMode === "QUOTATIONS_DETAILS" && activeTab === 4 && (
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <ButtonComp variant="contained" height={40} width={120}>
                Send
              </ButtonComp>
              <ButtonComp variant="outlined" height={40} width={120}>
                Print
              </ButtonComp>
              <ButtonComp
                variant="contained"
                color="secondary"
                height={40}
                width={120}
              >
                Edit
              </ButtonComp>
            </Box>
          )}
        </Box>
        {renderTabContent()}
      </Paper>

      <Dialog
        open={showConversionDialog}
        onClose={() => {
          if (!isConvertingClient) {
            setShowConversionDialog(false);
            resetConversionDialog();
          }
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "14px", overflow: "hidden" } }}
      >
        <Box sx={{ px: 3, py: 2.5, bgcolor: "#141E35", color: "#fff", borderBottom: "4px solid #EC6E00" }}>
          <Typography fontSize={11} fontWeight={900} color="#FFAA64" letterSpacing={1.1} textTransform="uppercase">
            Client Classification
          </Typography>
          <Typography fontSize={21} fontWeight={900}>Convert Cash Client to Policyholder</Typography>
          <Typography fontSize={12.5} color="rgba(255,255,255,0.72)" mt={0.5}>
            The existing case, client information, documents and history will be retained.
          </Typography>
        </Box>
        <DialogContent sx={{ p: 3, bgcolor: "#F8FAFC" }}>
          <Stack gap={2}>
            <Alert severity="info" sx={{ borderRadius: "8px" }}>
              Link only the death claim for the same deceased. Funeral planning can be activated only after that claim is approved.
            </Alert>
            <Stack direction={{ xs: "column", sm: "row" }} gap={1}>
              <TextField
                fullWidth
                size="small"
                label="Policy Number"
                placeholder="Enter the exact policy number"
                value={conversionPolicyNumber}
                disabled={isSearchingConversionClaim || isConvertingClient}
                onChange={(event) => {
                  setConversionPolicyNumber(event.target.value);
                  setConversionClaims([]);
                  setSelectedConversionClaim(null);
                  setConversionError("");
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    void searchConversionClaim();
                  }
                }}
              />
              <Button
                variant="contained"
                onClick={() => void searchConversionClaim()}
                disabled={isSearchingConversionClaim || isConvertingClient || !conversionPolicyNumber.trim()}
                sx={{ minWidth: 145, minHeight: 40, textTransform: "none", borderRadius: "8px", bgcolor: "#EC6E00", fontWeight: 800, "&:hover": { bgcolor: "#C45700" } }}
              >
                {isSearchingConversionClaim ? <CircularProgress size={20} color="inherit" /> : "Find Claim"}
              </Button>
            </Stack>

            {conversionClaims.map((claim: any) => {
              const selected = selectedConversionClaim?.claim_id === claim.claim_id;
              const blocked = ["DECLINED", "REJECTED"].includes(String(claim.status || ""));
              return (
                <Paper
                  key={claim.claim_id}
                  variant="outlined"
                  onClick={() => { if (!blocked && !isConvertingClient) setSelectedConversionClaim(claim); }}
                  sx={{
                    p: 1.5,
                    borderRadius: "10px",
                    cursor: blocked ? "not-allowed" : "pointer",
                    borderColor: selected ? "#EC6E00" : "#DDE3EC",
                    bgcolor: selected ? "#FFF7ED" : "#fff",
                    opacity: blocked ? 0.65 : 1,
                  }}
                >
                  <Stack direction="row" gap={1.25} alignItems="center">
                    <Radio checked={selected} disabled={blocked} size="small" sx={{ color: "#EC6E00", "&.Mui-checked": { color: "#EC6E00" } }} />
                    <Box flex={1}>
                      <Typography fontWeight={900} fontSize={14}>{claim.ice_claim_number || claim.claim_id}</Typography>
                      <Typography fontSize={12} color="#667085">
                        {claim.claimant_name || claim.main_member_name || "Claimant unavailable"} · Policy {claim.policy_number}
                      </Typography>
                    </Box>
                    <Chip size="small" label={String(claim.status || "UNKNOWN").replaceAll("_", " ")} sx={{ fontWeight: 800 }} />
                  </Stack>
                </Paper>
              );
            })}

            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Conversion Reason"
              placeholder="Example: Active policy confirmed after the cash case was opened"
              value={conversionReason}
              inputProps={{ maxLength: 500 }}
              disabled={isConvertingClient}
              onChange={(event) => { setConversionReason(event.target.value); setConversionError(""); }}
              helperText={`${conversionReason.length}/500 · Saved in the case audit history`}
            />
            {conversionError && <Alert severity="error" sx={{ borderRadius: "8px" }}>{conversionError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #E5E7EB" }}>
          <Button
            onClick={() => { setShowConversionDialog(false); resetConversionDialog(); }}
            disabled={isConvertingClient}
            sx={{ color: "#475467", textTransform: "none", fontWeight: 800 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleConvertCashClient()}
            disabled={isConvertingClient || !selectedConversionClaim || !conversionReason.trim() || !currentUserId}
            sx={{ minWidth: 190, textTransform: "none", borderRadius: "8px", bgcolor: "#EC6E00", fontWeight: 900, "&:hover": { bgcolor: "#C45700" } }}
          >
            {isConvertingClient ? <><CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />Converting...</> : "Confirm Conversion"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Complete handover dialog */}
<Dialog
  open={showDeliveryDialog}
  onClose={() => { if (!isDelivering) setShowDeliveryDialog(false); }}
  fullWidth
  maxWidth="xs"
  slotProps={{
    backdrop: {
      sx: {
        backdropFilter: "blur(8px)",
        backgroundColor: "rgba(0,0,0,0.35)",
      },
    },
  }}
  PaperProps={{
    sx: {
      borderRadius: 4,
      overflow: "hidden",
      boxShadow: "0 32px 80px rgba(0,0,0,0.22)",
      border: "1px solid rgba(255,255,255,0.1)",
    },
  }}
>
  {/* Orange Header */}
  <Box
    sx={{
      background: "linear-gradient(135deg, #EC6E00 0%, #ff9535 100%)",
      px: 3,
      pt: 3,
      pb: 3.5,
      position: "relative",
    }}
  >
    {/* Close Button */}
    <Box
      onClick={() => { if (!isDelivering) setShowDeliveryDialog(false); }}
      sx={{
        position: "absolute",
        top: 14,
        right: 14,
        width: 30,
        height: 30,
        borderRadius: "50%",
        bgcolor: "rgba(255,255,255,0.2)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        "&:hover": { bgcolor: "rgba(255,255,255,0.35)" },
        transition: "background 0.2s",
      }}
    >
      <Typography fontSize={14} color="#fff" lineHeight={1} fontWeight={600}>X</Typography>
    </Box>

    {/* Icon + Title */}
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: 2.5,
        bgcolor: "rgba(255,255,255,0.2)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mb: 1.5,
        fontSize: 22,
      }}
    >
      BR
    </Box>
    <Typography fontSize={22} fontWeight={800} color="#fff" lineHeight={1.2}>
      Complete Handover
    </Typography>
    <Typography fontSize={13} color="rgba(255,255,255,0.8)" mt={0.5}>
      Fill in the mortuary handover details
    </Typography>
  </Box>

  {/* Body */}
  <DialogContent sx={{ px: 3, py: 3, bgcolor: "#F6F7F9" }}>
    <Box display="flex" flexDirection="column" gap={2}>

      {/* Driver ID readonly */}
      <Box
        sx={{
          bgcolor: "#fff",
          border: "1px solid #EDEAE5",
          borderRadius: 2.5,
          px: 2.5,
          py: 1.5,
          position: "relative",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 10,
            right: 12,
            bgcolor: "#FFF0E6",
            color: "#EC6E00",
            fontSize: 10,
            fontWeight: 700,
            px: 1,
            py: 0.3,
            borderRadius: 1,
            letterSpacing: 0.5,
          }}
        >
          AUTO
        </Box>
        <Typography
          fontSize={10}
          fontWeight={700}
          color="#EC6E00"
          textTransform="uppercase"
          letterSpacing={1}
          mb={0.5}
        >
          Driver ID
        </Typography>
        <Typography
          fontSize={12}
          color="#666"
          sx={{ wordBreak: "break-all", fontFamily: "monospace", lineHeight: 1.5 }}
        >
          {deliveryDetails.driverId || "-"}
        </Typography>
      </Box>

      {/* Received By */}
      <Box
        sx={{
          bgcolor: "#fff",
          border: "1px solid #EDEAE5",
          borderRadius: 2.5,
          px: 2.5,
          py: 1.5,
          "&:focus-within": {
            borderColor: "#EC6E00",
            boxShadow: "0 0 0 3px rgba(236,110,0,0.1)",
          },
          transition: "all 0.2s",
        }}
      >
        <Typography
          fontSize={10}
          fontWeight={700}
          color="#EC6E00"
          textTransform="uppercase"
          letterSpacing={1}
          mb={0.5}
        >
          Received By <span style={{ color: "#EC6E00" }}>*</span>
        </Typography>
        <TextField
          variant="standard"
          fullWidth
          placeholder="Enter receiver's full name"
          value={deliveryDetails.receivedBy}
          onChange={handleDeliveryFieldChange("receivedBy")}
          disabled={isDelivering}
          InputProps={{ disableUnderline: true }}
          sx={{
            "& input": {
              fontSize: 14,
              fontWeight: 500,
              color: "#181C27",
              p: 0,
              "&::placeholder": { color: "#bbb", fontSize: 13 },
            },
          }}
        />
      </Box>

      {/* KM In */}
      <Box
        sx={{
          bgcolor: "#fff",
          border: "1px solid #EDEAE5",
          borderRadius: 2.5,
          px: 2.5,
          py: 1.5,
          "&:focus-within": {
            borderColor: "#EC6E00",
            boxShadow: "0 0 0 3px rgba(236,110,0,0.1)",
          },
          transition: "all 0.2s",
        }}
      >
        <Typography
          fontSize={10}
          fontWeight={700}
          color="#EC6E00"
          textTransform="uppercase"
          letterSpacing={1}
          mb={0.5}
        >
          KM In Arrival At Mortuary <span style={{ color: "#EC6E00" }}>*</span>
        </Typography>
        <TextField
          variant="standard"
          fullWidth
          type="number"
          placeholder="Enter odometer reading"
          value={deliveryDetails.kmInArrivalAtMortuary}
          onChange={handleDeliveryFieldChange("kmInArrivalAtMortuary")}
          disabled={isDelivering}
          InputProps={{ disableUnderline: true, inputProps: { min: 0 } }}
          sx={{
            "& input": {
              fontSize: 14,
              fontWeight: 500,
              color: "#181C27",
              p: 0,
              "&::placeholder": { color: "#bbb", fontSize: 13 },
            },
          }}
        />
        <Typography fontSize={11} color="#7A8594" mt={0.75}>
          KM Out: {bodyRemovalDetails?.kmOutFromMortuary ?? "-"}
        </Typography>
      </Box>

      {/* Handover Remarks */}
      <Box
        sx={{
          bgcolor: "#fff",
          border: "1px solid #EDEAE5",
          borderRadius: 2.5,
          px: 2.5,
          py: 1.5,
          "&:focus-within": {
            borderColor: "#EC6E00",
            boxShadow: "0 0 0 3px rgba(236,110,0,0.1)",
          },
          transition: "all 0.2s",
        }}
      >
        <Typography
          fontSize={10}
          fontWeight={700}
          color="#EC6E00"
          textTransform="uppercase"
          letterSpacing={1}
          mb={0.75}
        >
          Handover Remarks *
        </Typography>
        <TextField
          variant="standard"
          fullWidth
          multiline
          minRows={3}
          placeholder="Enter handover notes"
          value={deliveryDetails.remarks}
          onChange={handleDeliveryFieldChange("remarks")}
          disabled={isDelivering}
          InputProps={{ disableUnderline: true }}
          sx={{
            "& textarea": {
              fontSize: 14,
              fontWeight: 500,
              color: "#181C27",
              p: 0,
              "&::placeholder": { color: "#bbb", fontSize: 13 },
            },
          }}
        />
      </Box>

      {/* Info note */}
      <Box
        sx={{
          bgcolor: "#FFF8F3",
          border: "1px solid #FFE0C8",
          borderRadius: 2,
          px: 2,
          py: 1.25,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Typography fontSize={14} fontWeight={800}>i</Typography>
        <Typography fontSize={12} color="#9A5A2A">
          This action will update the mortuary register and cannot be undone.
        </Typography>
      </Box>
    </Box>
  </DialogContent>

  {/* Footer */}
  <DialogActions
    sx={{
      px: 3,
      py: 2.5,
      bgcolor: "#fff",
      borderTop: "1px solid #EDEAE5",
      gap: 1.5,
      justifyContent: "stretch",
    }}
  >
    <ButtonComp
      variant="outlined"
      height={44}
      width="100%"
      textTransform="none"
      onClick={() => setShowDeliveryDialog(false)}
      disabled={isDelivering}
    >
      Cancel
    </ButtonComp>
    <ButtonComp
      variant="contained"
      height={44}
      width="100%"
      textTransform="none"
      onClick={handleDeliverBody}
      disabled={isDelivering}
    >
      {isDelivering ? "Completing..." : "Complete Handover"}
    </ButtonComp>
  </DialogActions>
</Dialog>

      <VerifiedPopup
        label={verifiedLabel}
        open={showVerified}
        onClose={() => setShowVerified(false)}
      />
    </Box>
  );
}
